'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface HarmonicEditDialogProps {
  open: boolean;
  onClose: () => void;
  initialLabelPrefix: string;
  initialMultiplier: number;
  initialStartingPoint: number;
  initialInterval?: number;
  xAxisType: 'date' | 'numeric' | 'category';
  initialIntervalType?: 'seconds' | 'minutes' | 'hours' | 'days';
  onSave: (labelPrefix: string, multiplier: number, startingPoint: number, intervalType?: 'seconds' | 'minutes' | 'hours' | 'days', interval?: number) => void;
  position?: { x: number; y: number };
  xRange?: { min: number; max: number }; // Optional x-axis range for validation
  replaceMarkers?: boolean; // If true, creating harmonics/carriers will replace existing markers
  isNewGroup?: boolean; // If true, this is creating a new group (not editing)
}

/**
 * Popover for editing an existing harmonic group
 */
export const HarmonicEditDialog: React.FC<HarmonicEditDialogProps> = ({
  open,
  onClose,
  initialLabelPrefix,
  initialMultiplier,
  initialStartingPoint,
  initialInterval = 0,
  xAxisType,
  initialIntervalType = 'days',
  onSave,
  position,
  xRange,
  replaceMarkers = false,
  isNewGroup = false,
}) => {
  const [labelPrefix, setLabelPrefix] = useState(initialLabelPrefix);
  const [multiplier, setMultiplier] = useState(initialMultiplier);
  const [startingPoint, setStartingPoint] = useState(initialStartingPoint);
  const [interval, setInterval] = useState(initialInterval);
  const [intervalType, setIntervalType] = useState<'seconds' | 'minutes' | 'hours' | 'days'>(initialIntervalType);
  const [intervalError, setIntervalError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Unit conversion helpers for date intervals
  const msToUnit = (ms: number, unit: 'seconds' | 'minutes' | 'hours' | 'days'): number => {
    const conversions = {
      seconds: 1000,
      minutes: 60 * 1000,
      hours: 60 * 60 * 1000,
      days: 24 * 60 * 60 * 1000,
    };
    return ms / conversions[unit];
  };

  const unitToMs = (value: number, unit: 'seconds' | 'minutes' | 'hours' | 'days'): number => {
    const conversions = {
      seconds: 1000,
      minutes: 60 * 1000,
      hours: 60 * 60 * 1000,
      days: 24 * 60 * 60 * 1000,
    };
    return value * conversions[unit];
  };

  // Get display value (convert ms to selected unit for date axes)
  const getDisplayInterval = (): number => {
    if (xAxisType === 'date') {
      return msToUnit(interval, intervalType);
    }
    return interval;
  };

  // Set interval from display value (convert from unit to ms for date axes)
  const setDisplayInterval = (value: number) => {
    // Round to 1 decimal place
    const rounded = Math.round(value * 10) / 10;

    if (xAxisType === 'date') {
      setInterval(unitToMs(rounded, intervalType));
    } else {
      setInterval(rounded);
    }

    // Validate the interval
    validateInterval(rounded);
  };

  // Validate interval is within x-axis range
  const validateInterval = (displayValue: number) => {
    if (!xRange) {
      setIntervalError(null);
      return;
    }

    const actualInterval = xAxisType === 'date' ? unitToMs(displayValue, intervalType) : displayValue;
    const range = xRange.max - xRange.min;

    // Check if interval is too large (harmonics would be outside range)
    if (actualInterval * multiplier > range) {
      setIntervalError(`Interval too large. Max: ${xAxisType === 'date' ? msToUnit(range / multiplier, intervalType).toFixed(1) : (range / multiplier).toFixed(1)}`);
    } else if (displayValue <= 0) {
      setIntervalError('Interval must be greater than 0');
    } else {
      setIntervalError(null);
    }
  };

  useEffect(() => {
    if (open) {
      setLabelPrefix(initialLabelPrefix);
      setMultiplier(initialMultiplier);
      setStartingPoint(initialStartingPoint);
      setInterval(initialInterval);
      setIntervalType(initialIntervalType);

      // Validate initial interval
      const displayValue = xAxisType === 'date' ? msToUnit(initialInterval, initialIntervalType) : initialInterval;
      validateInterval(displayValue);
    }
  }, [open, initialLabelPrefix, initialMultiplier, initialStartingPoint, initialInterval, initialIntervalType, xAxisType]);

  // Click outside handler
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  const handleSave = () => {
    onSave(labelPrefix, multiplier, startingPoint, intervalType, interval);
    onClose();
  };

  if (!open) {
    return null;
  }


  // Simplified UI for numeric axis type
  if (xAxisType === 'numeric') {
    return (
      <div
        ref={popoverRef}
        style={{
          position: 'fixed',
          left: position?.x || 100,
          top: position?.y || 100,
          zIndex: 9999,
          maxHeight: 'calc(100vh - 40px)',
          overflowY: 'auto',
        }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-[#B1B9C3] dark:border-gray-700 p-4 w-[240px]">
          <h3 className="text-sm font-semibold mb-4 text-[#01152B] dark:text-gray-100">Edit Harmonics</h3>

          <div className="space-y-3">
            {/* Position */}
            <div className="flex items-center justify-between">
              <Label htmlFor="position" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
                Position
              </Label>
              <Input
                id="position"
                type="number"
                value={startingPoint.toFixed(1)}
                onChange={(e) => setStartingPoint(parseFloat(e.target.value))}
                className="w-[120px] text-center text-xs h-8 bg-[#F1F2F4] dark:bg-gray-700 border-0 text-[#01152B] dark:text-gray-100 font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:ring-0 focus:outline-none px-2"
                step="0.1"
              />
            </div>

            {/* Distance - HIDDEN for harmonics */}
            <div className="hidden">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="distance" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
                    Distance
                  </Label>
                  <Input
                    id="distance"
                    type="number"
                    value={getDisplayInterval().toFixed(1)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setDisplayInterval(0);
                        return;
                      }
                      const parsed = parseFloat(value);
                      if (!isNaN(parsed)) {
                        setDisplayInterval(parsed);
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure value is formatted to 1 decimal on blur
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value)) {
                        const rounded = Math.round(value * 10) / 10;
                        setDisplayInterval(rounded);
                      }
                    }}
                    className={`w-[120px] text-center text-xs h-8 bg-[#F1F2F4] dark:bg-gray-700 ${
                      intervalError
                        ? 'border-2 border-red-500 dark:border-red-400'
                        : 'border-0'
                    } text-[#01152B] dark:text-gray-100 font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:ring-0 focus:outline-none px-2`}
                    step="0.1"
                  />
                </div>
                {intervalError && (
                  <div className="text-[10px] text-red-600 dark:text-red-400 text-right">
                    {intervalError}
                  </div>
                )}
              </div>
            </div>

            {/* Name */}
            <div className="flex items-center justify-between">
              <Label htmlFor="name" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                value={labelPrefix}
                maxLength={4}
                onChange={(e) => setLabelPrefix(e.target.value.toUpperCase())}
                className="w-[120px] text-center text-xs h-8 bg-[#F1F2F4] dark:bg-gray-700 border-0 text-[#01152B] dark:text-gray-100 font-semibold focus:ring-0 focus:outline-none px-2"
                placeholder="BPFO"
              />
            </div>

            {/* Count */}
            <div className="flex items-center justify-between">
              <Label htmlFor="count" className="text-xs font-normal text-[#55687D] dark:text-gray-400">
                Count
              </Label>
              <div className="flex items-center justify-between bg-[#F1F2F4] dark:bg-gray-700 rounded-md border-0 h-8 px-3 w-[120px]">
                <button
                  type="button"
                  onClick={() => {
                    const newMultiplier = Math.max(1, multiplier - 1);
                    setMultiplier(newMultiplier);
                    // Re-validate with new multiplier
                    validateInterval(getDisplayInterval());
                  }}
                  className="text-[#55687D] dark:text-gray-400 hover:text-[#01152B] dark:hover:text-gray-200 text-base leading-none flex items-center justify-center flex-shrink-0"
                >
                  −
                </button>
                <div className="w-[20px] h-full flex items-center justify-center flex-shrink-0 flex-grow-0">
                  <span className="text-xs font-semibold text-[#01152B] dark:text-gray-100">
                    {multiplier}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newMultiplier = Math.min(20, multiplier + 1);
                    setMultiplier(newMultiplier);
                    // Re-validate with new multiplier
                    validateInterval(getDisplayInterval());
                  }}
                  className="text-[#55687D] dark:text-gray-400 hover:text-[#01152B] dark:hover:text-gray-200 text-base leading-none flex items-center justify-center flex-shrink-0"
                >
                  +
                </button>
              </div>
            </div>

            {/* Info Text */}
            {isNewGroup && replaceMarkers && (
              <div className="pt-2 pb-1">
                <p className="text-[10px] text-amber-600 dark:text-amber-400 leading-tight">
                  Creating this group will replace existing markers
                </p>
              </div>
            )}
            {isNewGroup && !replaceMarkers && (
              <div className="pt-2 pb-1">
                <p className="text-[10px] text-blue-600 dark:text-blue-400 leading-tight">
                  Creating this group will keep existing markers
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E6E8EC] dark:border-gray-700">
              <Button
                variant="ghost"
                onClick={onClose}
                className="h-8 px-4 text-xs font-normal text-[#55687D] dark:text-gray-400 hover:text-[#01152B] dark:hover:text-gray-200 hover:bg-transparent"
              >
                Close
              </Button>
              <Button
                onClick={handleSave}
                disabled={!!intervalError}
                className="h-8 px-4 text-xs font-semibold bg-[#6667DD] hover:bg-[#5556CC] text-white border-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#6667DD]"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original UI for date-based axis type
  return (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: position?.x || 100,
        top: position?.y || 100,
        zIndex: 9999,
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto',
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 w-[400px]">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Edit Harmonic Group</h3>

        <div className="space-y-4">
          {/* Starting Point */}
          <div className="space-y-2">
            <Label htmlFor="startingPoint">Starting Point (Date/Time)</Label>
            <Input
              id="startingPoint"
              type="datetime-local"
              value={new Date(startingPoint).toISOString().slice(0, 16)}
              onChange={(e) => setStartingPoint(new Date(e.target.value).getTime())}
              className="w-full"
            />
          </div>

          {/* Interval Type - only for date-based charts */}
          <div className="space-y-2">
            <Label htmlFor="intervalType">Interval Type</Label>
            <select
              id="intervalType"
              value={intervalType}
              onChange={(e) => setIntervalType(e.target.value as 'seconds' | 'minutes' | 'hours' | 'days')}
              className="w-full h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md focus:border-primary-500 focus:ring-primary-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="seconds">Seconds</option>
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400">Time interval between harmonics</p>
          </div>

          {/* Multiplier */}
          <div className="space-y-2">
            <Label htmlFor="multiplier">Multiplier (how many harmonics)</Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => setMultiplier(Math.max(1, multiplier - 1))}
              >
                −
              </Button>
              <Input
                id="multiplier"
                type="number"
                value={multiplier}
                onChange={(e) => setMultiplier(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                className="w-20 text-center"
                min={1}
                max={20}
              />
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => setMultiplier(Math.min(20, multiplier + 1))}
              >
                +
              </Button>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                (Will create {multiplier} lines)
              </span>
            </div>
          </div>

          {/* Label Prefix */}
          <div className="space-y-2">
            <Label htmlFor="labelPrefix">Label Prefix</Label>
            <Input
              id="labelPrefix"
              type="text"
              value={labelPrefix}
              maxLength={4}
              onChange={(e) => setLabelPrefix(e.target.value.toUpperCase())}
              className="w-24 text-center"
              placeholder="H"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Labels will be: {labelPrefix}0, {labelPrefix}1, {labelPrefix}2, ...
            </p>
          </div>

          {/* Info Text */}
          {isNewGroup && replaceMarkers && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Creating this group will replace existing markers
              </p>
            </div>
          )}
          {isNewGroup && !replaceMarkers && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Creating this group will keep existing markers
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-600">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} className="bg-primary-500 hover:bg-primary-600 text-white">
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
